import {
  FALL_WINBACK_CAMPAIGN_ID,
  FALL_WINBACK_CHECKOUT_GRACE_MS,
  FALL_WINBACK_MAILCHIMP_ACTIVATED_TAG,
  FALL_WINBACK_PREVIEW_PATH,
} from "config/campaignEntitlements";
import { normalizeDateValue } from "./trialStatus";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export {
  FALL_WINBACK_CAMPAIGN_ID,
  FALL_WINBACK_MAILCHIMP_ACTIVATED_TAG,
  FALL_WINBACK_PREVIEW_PATH,
};

export function getCampaignEntitlement(user = {}, campaignId = FALL_WINBACK_CAMPAIGN_ID) {
  const entitlements = user?.campaignEntitlements || {};
  const entitlement = entitlements[campaignId];
  return entitlement && typeof entitlement === "object" ? entitlement : null;
}

export function hasClaimedCampaignEntitlement(user = {}, campaignId = FALL_WINBACK_CAMPAIGN_ID) {
  return Boolean(getCampaignEntitlement(user, campaignId)?.claimedAt);
}

export function getCampaignEntitlementExpiresAt(user = {}, campaignId = FALL_WINBACK_CAMPAIGN_ID) {
  return normalizeDateValue(getCampaignEntitlement(user, campaignId)?.expiresAt);
}

export function isCampaignEntitlementActive(user = {}, campaignId = FALL_WINBACK_CAMPAIGN_ID, options = {}) {
  const entitlement = getCampaignEntitlement(user, campaignId);
  if (!entitlement?.claimedAt) return false;

  const expiresAt = normalizeDateValue(entitlement.expiresAt);
  if (!expiresAt) return false;

  const nowDate = normalizeDateValue(options.now) || new Date();
  return expiresAt.getTime() > nowDate.getTime();
}

export function isFallWinbackPreviewActive(user = {}, options = {}) {
  return isCampaignEntitlementActive(user, FALL_WINBACK_CAMPAIGN_ID, options);
}

export function getFallWinbackEntitlement(user = {}) {
  return getCampaignEntitlement(user, FALL_WINBACK_CAMPAIGN_ID);
}

export function getFallWinbackDiscountExpiresAt(user = {}) {
  const entitlement = getFallWinbackEntitlement(user);
  return normalizeDateValue(entitlement?.discountExpiresAt) || normalizeDateValue(entitlement?.expiresAt);
}

export function isFallWinbackDiscountCheckoutOpen(user = {}, options = {}) {
  const discountExpiresAt = getFallWinbackDiscountExpiresAt(user);
  if (!discountExpiresAt) return false;

  const nowDate = normalizeDateValue(options.now) || new Date();
  return nowDate.getTime() <= discountExpiresAt.getTime() + FALL_WINBACK_CHECKOUT_GRACE_MS;
}

export function getFallWinbackDaysRemaining(user = {}, options = {}) {
  const expiresAt = getCampaignEntitlementExpiresAt(user, FALL_WINBACK_CAMPAIGN_ID);
  if (!expiresAt) return null;

  const nowDate = normalizeDateValue(options.now) || new Date();
  return Math.max(0, Math.ceil((expiresAt.getTime() - nowDate.getTime()) / MS_PER_DAY));
}

export function getFallWinbackTrialStatusPresentation(user = {}, options = {}) {
  if (!isFallWinbackPreviewActive(user, options)) return null;

  const daysRemaining = getFallWinbackDaysRemaining(user, options);
  if (daysRemaining == null) return null;

  const daysLabel = daysRemaining === 1 ? "1 day" : `${daysRemaining} days`;
  const tone = daysRemaining <= 3 ? "urgent" : "default";
  const headline = daysRemaining === 1
    ? "Last day of your preview"
    : `${daysLabel} left`;

  return {
    daysRemaining,
    stage: "fall-winback-preview",
    headline,
    message: "Your welcome-back preview and 25% annual offer end at the same time.",
    pillLabel: `${daysLabel} left in Preview`,
    pillCompactLabel: daysRemaining === 1 ? "1d" : `${daysRemaining}d`,
    ctaLabel: "View offer",
    ctaPath: "/pricing?offer=fall-winback-2026",
    tone,
  };
}
